import { useEffect, useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  MenuItem,
  Typography,
  Autocomplete,
  FormControlLabel,
  Checkbox,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarJefedeProductos = () => {
  const navigate = useNavigate()
  const { state } = useLocation() // Recibimos la fila seleccionada desde la grilla
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Rescatamos el código original por si el usuario lo modifica
  const [jefecodigoViejo, setJefecodigoViejo] = useState("")

  // ==========================================================================
  // PASO 1: MANEJO DE ESTADO (State)
  // Estado local para los datos del formulario, incluyendo el usrcodigo
  // ==========================================================================
  const [formData, setFormData] = useState({
    jefecodigo: "",
    jefedescri: "",
    jefestatus: "A",
    jefecomisiona: false,
    usrcodigo: "",
    ciacodigo2: "",
    emcodemp: "",
  })

  useEffect(() => {
    // Precargar los inputs con los valores de la fila enviada desde la grilla
    if (state) {
      setFormData({
        jefecodigo: state.jefecodigo || "",
        jefedescri: state.jefedescri || "",
        jefestatus: state.jefestatus || "A",
        jefecomisiona: state.jefecomisiona === 1 || state.jefecomisiona === true,
        usrcodigo: state.usrcodigo || "",
        ciacodigo2: state.ciacodigo2 || "",
        emcodemp: state.emcodemp || "",
      })
      setJefecodigoViejo(state.jefecodigo || "")
    } else {
      navigate(-1) // Retorno de seguridad si entran directo a la URL
    }
  }, [state, navigate])

  // ==========================================================================
  // PASO 2: EXTRACCIÓN DEFENSIVA EN REACT QUERY
  // ==========================================================================
  const { data: rawUsuarios, isLoading: isLoadingUsuarios } = useQuery({
    queryKey: ["listaUsuarios_Editar"],
    queryFn: async () => {
      try {
        const response = await api.get("/JefedeProductos/getUsuariosActivos")

        // Extracción robusta para garantizar siempre un arreglo puro []
        const rawData = response?.data?.data || response?.data || response || []
        const listaValidada = Array.isArray(rawData) ? rawData : Array.isArray(rawData?.data) ? rawData.data : []

        return listaValidada
      } catch (error) {
        console.error("Error al cargar Usuarios:", error)
        return []
      }
    },
  })

  // Formateamos la lista validada para el Autocomplete
  const listaUsuarios = (rawUsuarios || []).map((item) => ({
    id: item.usrcodigo || item.id || "",
    label: item.label || `${item.usrcodigo || item.id || ""} - ${item.usrnombre || item.descri || ""}`.trim(),
  }))
  // ==========================================================================

  const {
    mutateAsync: SaveEdicionJefe,
    isPending: isSavingEdicion,
    isError,
  } = useMutation({
    queryKey: ["isEditingJefeProducto"],
    fn: async (data) => {
      const response = await api.post("/JefedeProductos/updateJefedeProductos", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  // Manejar cambios en los campos del formulario con limpieza y topes
  const handleInputChange = (field, value) => {
    let finalValue = value.toUpperCase()

    if (field === "jefecodigo") finalValue = finalValue.slice(0, 6)
    else if (field === "jefedescri") finalValue = finalValue.slice(0, 80)
    else if (field === "ciacodigo2") finalValue = finalValue.slice(0, 2)
    else if (field === "emcodemp") finalValue = finalValue.slice(0, 10)

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  const handleCheckboxChange = (field, checked) => {
    setFormData((prev) => ({ ...prev, [field]: checked }))
  }

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // Preparar payload tal cual lo espera el backend
    const payload = {
      ...formData,
      jefecodigoOld: jefecodigoViejo,
      jefecodigoNew: formData.jefecodigo,
      jefecomisiona: formData.jefecomisiona ? 1 : 0,
    }

    try {
      if (!payload.jefecodigoNew.trim()) return showWarning("El Código del Jefe de Producto es obligatorio")
      if (!payload.jefedescri.trim()) return showWarning("La Descripción del Jefe de Producto es obligatoria")

      await SaveEdicionJefe(payload)
    } catch (error) {
      console.error("Error al editar el Jefe de Producto:", error)
      if (error?.code === "VALIDATION_ERROR") {
        showWarning(error.message)
      }
    }
  }

  // Acción dinámica de la barra de menú basada en permisos
  const actualizarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action?.acccaption === "GRABAR")

  const toolbarActions = actualizarAction
    ? [
        {
          label: actualizarAction.acccaption,
          key: actualizarAction.acccaption,
          icon: getIconComponent(actualizarAction.accnameicono, actualizarAction.acctipoico),
        },
      ]
    : [{ label: "Actualizar", key: "ACTUALIZAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* Toolbar superior con el botón de Guardar/Actualizar */}
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingEdicion || isLoadingUsuarios}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mr: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Editar Jefe de Producto: {jefecodigoViejo}</b>
        </div>

        <CustomBackdrop isLoading={isSavingEdicion || isLoadingUsuarios} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos del Jefe de Producto
            </Typography>
            <Grid container spacing={3}>
              {/* CÓDIGO */}
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código Jefe *"
                  value={formData.jefecodigo}
                  onChange={(e) => handleInputChange("jefecodigo", e.target.value)}
                  error={isError && !formData.jefecodigo}
                  inputProps={{ maxLength: 6 }}
                />
              </Grid>

              {/* DESCRIPCIÓN */}
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.jefedescri}
                  onChange={(e) => handleInputChange("jefedescri", e.target.value)}
                  error={isError && !formData.jefedescri}
                  inputProps={{ maxLength: 80 }}
                />
              </Grid>

              {/* ESTADO */}
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Estado *"
                  value={formData.jefestatus}
                  onChange={(e) => setFormData((prev) => ({ ...prev, jefestatus: e.target.value }))}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>

              {/* ==========================================================================
                  PASO 3: CONFIGURACIÓN ESTRICTA DEL COMPONENTE <Autocomplete>
              ========================================================================== */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  // 1. options: Nunca pasar undefined
                  options={listaUsuarios}
                  // 2. getOptionLabel: Manejar casos nulos o sin propiedad
                  getOptionLabel={(option) => option.label || ""}
                  // 3. value: Vincular con estado y fallback a null
                  value={listaUsuarios.find((c) => c.id === formData.usrcodigo) || null}
                  // 4. isOptionEqualToValue: OBLIGATORIO comparar por llave primaria
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  // 5. onChange: Actualizar el estado de forma segura (PASO 4 implícito)
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      usrcodigo: newValue ? newValue.id : "",
                    }))
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Usuario Asignado"
                      placeholder="Seleccione un usuario..."
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>

              {/* CÓDIGO COMPAÑÍA 2 */}
              <Grid item xs={12} sm={2}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Cía 2"
                  value={formData.ciacodigo2}
                  onChange={(e) => handleInputChange("ciacodigo2", e.target.value)}
                  inputProps={{ maxLength: 2 }}
                />
              </Grid>

              {/* CÓDIGO EMPLEADO */}
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Cód. Empleado"
                  value={formData.emcodemp}
                  onChange={(e) => handleInputChange("emcodemp", e.target.value)}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              {/* CHECKBOX COMISIONA */}
              <Grid item xs={12} sm={3} sx={{ display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.jefecomisiona}
                      onChange={(e) => handleCheckboxChange("jefecomisiona", e.target.checked)}
                      color="primary"
                    />
                  }
                  label="¿Comisiona?"
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EditarJefedeProductos
